<script setup lang="ts">
import { computed, watch } from 'vue'

import {
  useCreativeCostPreview,
  type CreativeCostPreviewInput,
  type CreativePreviewChange,
  type CreativeType,
} from '../../composables/useCreativeCostPreview'
import { formatCredits } from '../../service/creditPresentation'
import { summarizeCreativePreview } from '../../service/creativePreviewState'
import { useCreditStore } from '../../stores/creditStore'

import { useI18n } from '@/shared/i18n'
import { Icon, Spinner } from '@/shared/ui'

const props = defineProps<{
  creativeType: CreativeType
  dateFrom?: string
  dateTo?: string
  filters?: unknown
}>()

const emit = defineEmits<{
  previewChange: [change: CreativePreviewChange]
  previewLoading: [loading: boolean]
}>()

const { t } = useI18n()
const creditStore = useCreditStore()
const previewInput = computed<CreativeCostPreviewInput>(() => ({
  creativeType: props.creativeType,
  ...(props.dateFrom === undefined ? {} : { dateFrom: props.dateFrom }),
  ...(props.dateTo === undefined ? {} : { dateTo: props.dateTo }),
  ...(props.filters === undefined ? {} : { filters: props.filters }),
}))
const { preview, state, loading, hasConfig, chargeable, cost } = useCreativeCostPreview(
  previewInput,
  (change) => emit('previewChange', change),
)
watch(loading, (value) => emit('previewLoading', value), { immediate: true })

const summary = computed(() =>
  preview.value ? summarizeCreativePreview(preview.value, creditStore.remaining) : null,
)
const amounts = computed(() => {
  const value = summary.value
  if (!value) return {}
  return {
    total: value.total,
    chargeable: value.chargeable,
    count: value.affordableCount,
    unlabelled: Math.max(0, value.chargeable - value.affordableCount),
    remaining: formatCredits(value.remaining),
    required: formatCredits(value.totalRequired),
    shortfall: formatCredits(value.shortfall),
    analysisCost: formatCredits(value.analysisCost),
  }
})
// Only the empty-scope copy names the creative type ("No images match…"); the rest say "creatives".
function shortfallKey(part: 'title' | 'body'): string {
  const kind = summary.value?.kind ?? 'affordable'
  const base = `credits.creative.shortfall.${kind}.${part}`
  return kind === 'emptyScope' ? `${base}.${props.creativeType}` : base
}
const showsAnalysisCost = computed(
  () => Boolean(preview.value?.analysis) && summary.value?.kind !== 'analysisShortfall',
)

// Legacy parity: the leading count renders bold on its own; this only supplies "new image(s)"
// / "new video(s)" pluralised by that same count.
const newItemLabel = computed(() =>
  props.creativeType === 'image'
    ? t('credits.creative.newImage', { count: chargeable.value })
    : t('credits.creative.newVideo', { count: chargeable.value }),
)
</script>

<template>
  <div v-if="hasConfig" class="creative-cost-preview text-[0.875rem]" aria-live="polite">
    <div
      v-if="state === 'loading'"
      class="creative-cost-preview__row creative-cost-preview__muted flex items-center gap-xs text-muted"
    >
      <Spinner size="xs" aria-hidden="true" />
      <span aria-busy="true">{{ t('credits.creative.estimating') }}</span>
    </div>

    <div
      v-else-if="state === 'error'"
      class="creative-cost-preview__row creative-cost-preview__error flex items-center gap-xs text-danger"
    >
      <Icon name="alert-triangle" :size="12" aria-hidden="true" />
      <span>{{ t('credits.creative.previewFailed') }}</span>
    </div>

    <div v-else-if="preview && summary" class="creative-cost-preview__stack grid gap-1">
      <template v-if="summary.kind === 'affordable'">
        <div class="creative-cost-preview__row flex items-center gap-xs">
          <Icon name="coins" :size="12" aria-hidden="true" />
          <span v-if="summary.chargeable === 0" class="creative-cost-preview__muted text-muted">
            {{ t('credits.creative.allLabeled') }}
          </span>
          <span v-else class="creative-cost-preview__muted text-muted">
            <strong>{{ summary.chargeable }}</strong> {{ newItemLabel }}
            {{ t('credits.creative.arrow') }} <strong>{{ formatCredits(cost) }}</strong>
            {{ t('credits.creative.creditWord', { count: cost }) }}
            <template v-if="summary.alreadyLabeled">
              {{ t('credits.creative.alreadyLabeledInline', { count: summary.alreadyLabeled }) }}
            </template>
          </span>
        </div>
      </template>
      <template v-else>
        <p
          class="creative-cost-preview__warning m-0 text-warning font-semibold"
          :data-kind="summary.kind"
        >
          <Icon name="alert-triangle" :size="12" aria-hidden="true" />
          {{ t(shortfallKey('title'), amounts) }}
        </p>
        <p class="creative-cost-preview__muted text-muted">
          {{ t(shortfallKey('body'), amounts) }}
        </p>
      </template>

      <div
        v-if="showsAnalysisCost && preview.analysis"
        class="creative-cost-preview__row creative-cost-preview__muted flex items-center gap-xs text-muted"
      >
        <Icon name="database" :size="12" aria-hidden="true" />
        <span>{{ t('credits.creative.analysisCost', { count: preview.analysis.cost }) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.creative-cost-preview .creative-cost-preview__row :deep(.ui-icon) {
  color: var(--ui-color-primary);
}
.creative-cost-preview .creative-cost-preview__muted strong {
  color: var(--ui-color-fg);
  font-weight: 600;
}
.creative-cost-preview .creative-cost-preview__warning :deep(.ui-icon) {
  color: var(--ui-color-warning);
}
.creative-cost-preview p {
  margin: 0;
}
.creative-cost-preview .creative-cost-preview__error :deep(.ui-icon) {
  color: var(--ui-color-danger);
}
</style>
